import clsx from "clsx";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    ServiceAuthToken, updateNodeToken,
    useGetServiceAuthTokensForService
} from "../../api/authTokens";
import { useGetAddedIntegrations } from "../../api/integrations";
import {
    useDeleteNodeById, useDeleteNodeExecutionCondition,
    useGetConfigsForNode, useGetNodeTestRunInstance,
    usePatchNodeExecutionCondition, usePostNodeExecutionCondition
} from "../../api/nodeApi";
import { useGetActionsForService } from "../../api/services";
import { IdValueOption, ResponseConfigOption } from "../../types/common";
import { NodeConfig } from "../../types/nodes";
import { ServiceActions } from "../../types/service";
import {
    InputDataRow,
    NodeExecutionCondition, NodeRunInstance,
    NodeTestRunInstance, RunInstance, WorkflowNode
} from "../../types/workflow";
import customFetch from "../../utils/customFetch";
import { useWorkflowId } from "../../utils/misc";
import WorkflowAccordion from "../Common/WorkflowAccordion";
import RunTest from "../RunTest";
import SelectAction from "./SelectAction";
import TokenSelection from './TokenSelection';
import TriggerSetup from "./TriggerSetup";
import TriggerSetupCodeNode from "./TriggerSetupCodeNode";
import WorkflowBoxHeader from './WorkflowBoxHeader';
import TriggerSetupWaitForConfirmation from "./TriggerSetupWaitForConfirmation";
import TriggerSetupNanonetsExtractDataFromFiles from "./TriggerSetupNanonetsExtractDataFromFiles";
import TriggerSetupSalesforceCreateCase from "./TriggerSetupSalesforceCreateCase";
import TriggerSetupNanonetsUpdate from "./TriggerSetupNanonetsUpdate";
import TriggerSetupSharepointUpdateMetadata from "./TriggerSetupSharepointUpdateMetadata";
import OneShotSamplesPopper from './OneShotSamplesPopper';
import { Button } from '@mui/material';

const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface TokenOption {
    id: string;
    type: 'service' | 'bot';
    serviceUniqueId: string;
    serviceReadableInfo: string;
    label: string;
};

interface AuthToken {
    id: string;
    service_id: string;
    service_unique_id: string;
    service_readable_info: string;
    access_token: string;
    refresh_token: string;
    token_type: string;
    expiry: string;
};

const useWorkflowBoxMutations = () => {
    const { mutate: getActionsForService } = useGetActionsForService();
    const { mutate: postNodeExecutionCondition } = usePostNodeExecutionCondition();
    const { mutate: patchNodeExecutionCondition } = usePatchNodeExecutionCondition();
    const { mutate: deleteNodeExecutionCondition } = useDeleteNodeExecutionCondition();
    const { mutate: deleteNodeById } = useDeleteNodeById();

    return {
        getActionsForService,
        postNodeExecutionCondition,
        patchNodeExecutionCondition,
        deleteNodeExecutionCondition,
        deleteNodeById,
    }
}

// Value of node will belong here
export type WorkflowBoxProps = {
    workflowType: string;
    workflowTitle?: string;
    workflowBoxIndex: number;
    openByDefault?: boolean;
    keepAlwaysOpen?: boolean;
    additionalClasses?: string;
    savedAction?: string;
    onNodeSave: (isIntermittentSave: boolean, id: string, chosenAction: string, workflowType: string, refreshSavedRunConfig: (nodeId: string) => void, cb?: () => void) => void;
    nodeIdsAbove?: {id: string, service: string, name: string}[];
    workflowNode: WorkflowNode;
    nodeConfigs: Record<string, NodeConfig>;
    shouldDeleteNode: () => void;
    updateNodeConfigsFromRunInstances: (nodeId: string, response_config: string, nodeConfigs?: Record<string, NodeConfig>) => void;
    testRunInputs?: {run_instance: RunInstance, node_run_instances: NodeRunInstance[]}[] | [];
    selectedRunInstanceId: string;
    setSelectedRunInstanceId: (value: string) => void;
    refetchTestRunInputs: (params: { nodeId: string, reset: boolean }, options?: { onSettled: () => void }) => void;
    setNodeConfigs: React.Dispatch<React.SetStateAction<Record<string, NodeConfig>>>;
    nodeResponseConfigs: Record<string, Record<string, string>>;
    setNodeResponseConfigs: React.Dispatch<React.SetStateAction<Record<string, Record<string, string>>>>;
    setNodeConfigsForNodeInRef: (nodeId: string, newNodeConfigs: NodeConfig) => void;
    getNodeConfigsForNodeFromRef: (nodeId: string) => NodeConfig;
    saveConfigField: (nodeId: string, key: string, refreshSavedRunConfig: (nodeId: string) => void, setIsFetchingSavedNodeConfig: (value: boolean) => void) => void;
    executionConditionsForNode: NodeExecutionCondition[];
    topNodeIdRef: React.MutableRefObject<string>;
    isTriggerNode: boolean;
    parentIsTrigger: boolean;
    triggerNodeId: string,
    refreshWorkflowNodes: () => void,
    setExecutionConditionsForNodes: React.Dispatch<React.SetStateAction<Record<string, NodeExecutionCondition[]>>>;
    getHasChildren: (node: WorkflowNode) => boolean;
    workflowNodes: WorkflowNode[];
    testRunInstances?: Record<string, NodeTestRunInstance>;
}

const WorkflowBox = ({
    workflowType,
    workflowTitle,
    workflowBoxIndex,
    openByDefault = false,
    keepAlwaysOpen,
    additionalClasses,
    savedAction,
    onNodeSave,
    nodeIdsAbove,
    workflowNode,
    nodeConfigs,
    shouldDeleteNode,
    updateNodeConfigsFromRunInstances,
    testRunInputs,
    selectedRunInstanceId,
    setSelectedRunInstanceId,
    refetchTestRunInputs,
    nodeResponseConfigs,
    setNodeResponseConfigs,
    setNodeConfigs,
    setNodeConfigsForNodeInRef,
    getNodeConfigsForNodeFromRef,
    saveConfigField,
    executionConditionsForNode,
    topNodeIdRef,
    isTriggerNode,
    parentIsTrigger,
    triggerNodeId,
    refreshWorkflowNodes,
    setExecutionConditionsForNodes,
    getHasChildren,
    workflowNodes,
    testRunInstances,
}: WorkflowBoxProps) => {
    const workflowId = useWorkflowId();
    const {
        getActionsForService,
        postNodeExecutionCondition,
        patchNodeExecutionCondition,
        deleteNodeExecutionCondition,
        deleteNodeById,
    } = useWorkflowBoxMutations();
    const { data: integrations } = useGetAddedIntegrations();
    const { mutate: fetchSavedNodeConfig } = useGetConfigsForNode();
    const { data: testRunInstance, refetch: refetchTestRunInstance } = useGetNodeTestRunInstance(workflowNode.node?.id);
    const [isFetchingSavedNodeConfig, setIsFetchingSavedNodeConfig] = useState(false);
    const { data: serviceAuthTokens } = useGetServiceAuthTokensForService(workflowType);

    const nodeId = workflowNode.node?.id || "";
    const service = workflowNode.node?.service || "";

    const nodeRunConfig = nodeConfigs?.[workflowNode.node?.id as string]?.run_config || {};
    const nodeRunConfigMetadata = nodeConfigs?.[workflowNode.node?.id as string]?.run_config_metadata || {};
    const actionMetadata = nodeConfigs?.[workflowNode.node?.id as string]?.action_metadata || {};

    const [isSomeTriggerAdded, setIsSomeTriggerAdded] = useState(false);
    const [nodeRunConfigHasKeys, setNodeRunConfigHasKeys] = useState(false);

    const [expandedSection, _setExpandedSection] = useState('');
    const [currentToken, setCurrentToken] = useState<TokenOption | null>(null);

    const serviceTokens = Array.isArray(serviceAuthTokens) ? serviceAuthTokens : [];
    const tokenOptions = useMemo(
        () => serviceTokens.map(
            (token: ServiceAuthToken) => ({
            id: token.id,
            type: 'service' as const,
            serviceUniqueId: token.service_unique_id,
            serviceReadableInfo: token.service_readable_info,
            label: `${(token.service_readable_info || token.service_unique_id)?
                `${token.service_readable_info || token.service_unique_id}` + ' - '
                : ''}`+`${token.id}`
        })), [serviceTokens]); // Only recalculate when serviceTokens changes

    // Set current token on mount
    useEffect(() => {
        if (nodeId && nodeConfigs[nodeId]) {
            const { service_auth_token_id } = nodeConfigs[nodeId];

            if (service_auth_token_id) {
                const token = tokenOptions.find(
                    t => t.id === service_auth_token_id &&
                    t.type === 'service');
                console.log("Found service token:", token);
                setCurrentToken(token || null);
            } else {
                setCurrentToken(null);
            }
        }
    }, [nodeId, nodeConfigs, tokenOptions]);

    useEffect(() => {
        _setExpandedSection('Choose event');
    }, [nodeId]);

    // Handle token change
    const handleTokenChange = async (token: TokenOption | null) => {
        console.log("handleTokenChange called with:", token);

        setCurrentToken(token);
        if (token) {
            if (!nodeId) {
                console.log("No node ID found");
                return;
            }

            try {
                console.log("Updating node token:", {
                    nodeId,
                    serviceAuthTokenId: token.type === 'service' ? token.id : "",
                    botAuthTokenId: token.type === 'bot' ? token.id : ""
                });

                await updateNodeToken({
                    nodeId,
                    serviceAuthTokenId: token.type === 'service' ? token.id : ""
                });

                // Update local nodeConfigs state
                setNodeConfigs(prev => ({
                    ...prev,
                    [nodeId]: {
                        ...prev[nodeId],
                        service_auth_token_id: token.type === 'service' ? token.id : undefined
                    }
                }));

                setCurrentToken(token);
                refreshWorkflowNodes();
                refreshSavedRunConfig(nodeId);
            } catch (error) {
                console.error('Failed to update node token:', error);
                // Handle error (show notification, etc.)
            }
        }
    };

    const setExpandedSection = (section: string) => {
        if (section === expandedSection) {
            _setExpandedSection('');
        } else {
            _setExpandedSection(section);
        }
    }

    const [workflowNodesMap, setWorkflowNodesMap] = useState<Record<string, WorkflowNode>>({});

    useEffect(() => {
        setWorkflowNodesMap(workflowNodes.reduce((acc, node) => {
            const nodeId = node?.node?.id as string;
            if (nodeId) {
                acc[nodeId] = node;
            }
            return acc;
        }, {} as Record<string, WorkflowNode>));
    }, [workflowNodes]);

    useEffect(() => {
        const nodeId = workflowNode.node?.id;
        if (nodeId) {
            refreshSavedRunConfig(nodeId);
        }
    }, [fetchSavedNodeConfig, workflowNode.node?.id]);

    const refreshSavedRunConfig = (nodeId: string) => {
        fetchSavedNodeConfig({ nodeId }, {
            onSuccess: (res) => {
                setNodeConfigs(
                    (prev: Record<string, NodeConfig>) => ({
                        ...prev,
                        [nodeId]: res
                    })
                );
                setNodeConfigsForNodeInRef(nodeId, res);
                // setNodeResponseConfigs(
                //     (prev: Record<string, Record<string, string>>) => ({
                //         ...prev,
                //         [nodeId]: res.response_config
                //     })
                // );
                const nodeRunConfig = getNodeConfigsForNodeFromRef(nodeId)?.run_config;
                setIsSomeTriggerAdded(
                    nodeRunConfig && !!Object.keys(nodeRunConfig)?.length &&
                        Object.values(nodeRunConfig).some(value => value && String(value)?.trim())
                );
                setNodeRunConfigHasKeys(nodeRunConfig && !!Object.keys(nodeRunConfig)?.length);
            },
            onSettled: () => {
                setIsFetchingSavedNodeConfig(false); // Reset loading state
            }
        });
    }

    const setNodeRunConfigAndRefNodeId = (key: string, value: string, refNodeId?: string) => {
        const nodeId = workflowNode.node?.id;
        if (nodeId) {
            const nodeConfigsForNode = getNodeConfigsForNodeFromRef(nodeId);
            setNodeConfigsForNodeInRef(nodeId, {
                ...nodeConfigsForNode,
                run_config: {
                    ...nodeConfigsForNode.run_config,
                    [key]: value
                },
                run_config_metadata: {
                    ...nodeConfigsForNode.run_config_metadata,
                    [key]: {
                        ...nodeConfigsForNode.run_config_metadata?.[key],
                        ref_node_id: refNodeId
                    }
                }
            });
            const nodeRunConfig = getNodeConfigsForNodeFromRef(nodeId)?.run_config;
            setIsSomeTriggerAdded(
                nodeRunConfig && !!Object.keys(nodeRunConfig)?.length &&
                    Object.values(nodeRunConfig).some(value => value && String(value)?.trim())
            );
            setNodeRunConfigHasKeys(!!Object.keys(nodeRunConfig)?.length);
        }
    }

    const resetNodeRunConfigInRef = (runConfig: Record<string, string>, runConfigMetadata: Record<string, {ref_node_id: string}>) => {
        const nodeId = workflowNode.node?.id;
        if (nodeId) {
            setNodeConfigsForNodeInRef(nodeId, {
                ...getNodeConfigsForNodeFromRef(nodeId),
                run_config: runConfig,
                run_config_metadata: runConfigMetadata
            });
            const nodeRunConfig = getNodeConfigsForNodeFromRef(nodeId)?.run_config;
            setIsSomeTriggerAdded(
                nodeRunConfig && !!Object.keys(nodeRunConfig)?.length &&
                    Object.values(nodeRunConfig).some(value => value && String(value)?.trim())
            );
            setNodeRunConfigHasKeys(!!Object.keys(nodeRunConfig)?.length);
        }
    }

    const [openDetails, setOpenDetails] = useState(openByDefault);
    const [openConditionsDetails, setConditionsOpenDetails] = useState(openByDefault);
    const [actionsForService, setActionsForService] = useState<ServiceActions>({});
    // const [chosenAction, setChosenAction] = useState(savedAction || '');
    const [responseConfig, setResponseConfig] = useState<IdValueOption[]>([]);
    const [localConditions, _setLocalConditions] = useState<NodeExecutionCondition[]>([]); // Local state to manage conditions
    const localConditionsRef = useRef<NodeExecutionCondition[]>([]);

    const setLocalConditions = (conditions: NodeExecutionCondition[] | ((prev: NodeExecutionCondition[]) => NodeExecutionCondition[])) => {
        if (typeof conditions === 'function') {
            _setLocalConditions((prev) => {
                const newConditions = conditions(prev);
                localConditionsRef.current = newConditions;
                return newConditions;
            });
        } else {
            _setLocalConditions(conditions);
            localConditionsRef.current = conditions;
        }
    }


    const [executionConditionsToDelete, setExecutionConditionsToDelete] = useState<string[]>([]);
    const [menuAnchorEl, setMenuAnchorEl] = useState<SVGSVGElement | null>(null);
    // TODO: change this logic
    const isIntegrationPresent = !!workflowNode.node?.service_auth_token_id;
    const [inputRows, setInputRows] = useState<InputDataRow[]>([]); // input values section for code node for now
    const chosenAction = workflowNode?.node?.action || '';

    let isAnyStepIncomplete = Boolean(
        // No action chosen
        !chosenAction ||
            // No integration present and service is not webhook, delay, filter
            // or code
            (
                !isIntegrationPresent &&
                workflowNode?.node?.service &&
                ["webhook", "sesinbound", "delay", "filter", "code", "chatgpt", "postgres", "nanonets", "pdf", "schedule", "scraper", "review", "browserAIAgent"].indexOf(
                    workflowNode?.node.service as string) === -1
            ) ||
            // TODO: use better check
            // No trigger added and node service is not filter
            (
                (nodeRunConfigHasKeys &&
                    !isSomeTriggerAdded &&
                    workflowNode?.node.service !== "filter"
                )
            )
    );



    isAnyStepIncomplete = false;

    useEffect(() => {
        if (Array.isArray(executionConditionsForNode)) {
            setLocalConditions([...executionConditionsForNode]);
        }
    }, [executionConditionsForNode]);

    const onSave = (action?: string, cb?: () => void, isIntermittentSave?: boolean) => {
        if (workflowType === 'code') {
            const nodeConfigInRef = getNodeConfigsForNodeFromRef(nodeId);
            if (nodeConfigInRef) {
                let { run_config } = nodeConfigInRef;
                run_config = run_config || {}

                const validInputRows = inputRows.filter(row => row.key && row.value);

                let runConfig = validInputRows.reduce((acc, row) => ({...acc, [row.key]: row.value}), {});
                runConfig = {
                    ...runConfig,
                    code: run_config.code
                }
                const runConfigMetadata = inputRows.reduce((acc, row) => ({...acc, [row.key]: {ref_node_id: row.refNodeId}}), {});
                resetNodeRunConfigInRef(runConfig, runConfigMetadata);
            }
        }

        if (workflowType === 'review' && chosenAction === 'WaitForConfirmation') {
            const nodeConfigInRef = getNodeConfigsForNodeFromRef(nodeId);
            if (nodeConfigInRef) {
                let { run_config } = nodeConfigInRef;
                run_config = run_config || {}

                const validInputRows = inputRows.filter(row => row.key && row.value);

                let runConfig = validInputRows.reduce((acc, row) => ({...acc, [row.key]: row.value}), {});
                runConfig = {
                    ...runConfig,
                    post_confirmation_endpoint: run_config.post_confirmation_endpoint
                }
                const runConfigMetadata = inputRows.reduce((acc, row) => ({...acc, [row.key]: {ref_node_id: row.refNodeId}}), {});
                resetNodeRunConfigInRef(runConfig, runConfigMetadata);
            }
        }

        if (workflowType === 'nanonets' && chosenAction === 'ExtractDataFromFiles') {
            const nodeConfigInRef = getNodeConfigsForNodeFromRef(nodeId);
            if (nodeConfigInRef) {
                let { run_config } = nodeConfigInRef;
                run_config = run_config || {}

                const validInputRows = inputRows.filter(row => row.key && row.value);

                let runConfig = validInputRows.reduce((acc, row) => ({...acc, [row.key]: row.value}), {});
                runConfig = {
                    ...runConfig,
                    api_key: run_config.api_key,
                    file_urls: run_config.file_urls,
                    merge_files: run_config.merge_files,
                    model_id: run_config.model_id,
                }
                const runConfigMetadata = inputRows.reduce((acc, row) => ({...acc, [row.key]: {ref_node_id: row.refNodeId}}), {});
                resetNodeRunConfigInRef(runConfig, runConfigMetadata);
            }
        }

        if (workflowType === 'salesforce' && chosenAction === 'CreateCase') {
            const nodeConfigInRef = getNodeConfigsForNodeFromRef(nodeId);
            if (nodeConfigInRef) {
                let { run_config } = nodeConfigInRef;
                run_config = run_config || {}

                const validInputRows = inputRows.filter(row => row.key && row.value);

                let runConfig = validInputRows.reduce((acc, row) => ({...acc, [row.key]: row.value}), {});
                runConfig = {
                    ...runConfig,
                    endpoint: run_config.endpoint,
                    client_id: run_config.client_id,
                    client_secret: run_config.client_secret,
                }
                const runConfigMetadata = inputRows.reduce((acc, row) => ({...acc, [row.key]: {ref_node_id: row.refNodeId}}), {});
                resetNodeRunConfigInRef(runConfig, runConfigMetadata);
            }
        }

        localConditionsRef.current?.forEach((executionCondition, index) => {
            if(executionCondition.id) {
                patchNodeExecutionCondition({ payload: executionCondition }, {
                    onSuccess: (executionConditionResponse) => {
                        const _conditions = [...localConditionsRef.current];
                        _conditions[index] = {...executionConditionResponse};
                        setLocalConditions(() => {
                            setExecutionConditionsForNodes((prev) => {
                                return {
                                    ...prev,
                                    [nodeId]: _conditions
                                }
                            });
                            localConditionsRef.current = _conditions;
                            return _conditions;
                        });
                    }
                });
            } else {
                if (executionCondition.node_data_key) {
                    postNodeExecutionCondition({ payload: executionCondition }, {
                        onSuccess: (executionConditionResponse) => {
                            const _conditions = [...localConditionsRef.current];
                            _conditions[index] = {...executionConditionResponse};
                            setLocalConditions(() => {
                                setExecutionConditionsForNodes((prev) => {
                                    return {
                                        ...prev,
                                        [nodeId]: _conditions
                                    }
                                });
                                localConditionsRef.current = _conditions;
                                return _conditions;
                            });
                        }
                    });
                }
            }
        });
        executionConditionsToDelete?.forEach(conditionId => deleteNodeExecutionCondition({ executionId: conditionId }));
        onNodeSave(!!isIntermittentSave, nodeId, action || chosenAction, service, refreshSavedRunConfig, cb);
    };

    useEffect(() => {
        getActionsForService({ service: workflowType }, {
            onSuccess: (services) => setActionsForService(services)
        });
    }, [getActionsForService, workflowType]);

    const [countToNameMap, setCountToNameMap] = useState<Record<string, string>>({});

    useEffect(() => {
        const nodeIdsAboveLength = nodeIdsAbove?.length;
        if (nodeIdsAboveLength && nodeConfigs) {
            const responseConfigArray: ResponseConfigOption[] = [];
            nodeIdsAbove.forEach((presentNode, index) => {
                const _responseConfig = nodeResponseConfigs?.[presentNode.id];
                if (_responseConfig && Object.keys(_responseConfig)?.length) {
                    Object.entries(_responseConfig).forEach(([configKey, configValue]) => {
                        // If we support dependency on multiple nodes
                        // for the same config key, we would have multiple refNodes
                        configKey && responseConfigArray.push({
                            id: configKey,
                            value: configValue || "",
                            valueDetail: presentNode.service,
                            count: index + 1,
                            refNodeId: presentNode.id,
                            nodeName: presentNode?.name,
                        });
                    });
                } else {
                    // hacky way to show the run test ui section
                    if (["filter", "delay"].indexOf(presentNode.service) === -1) {
                        responseConfigArray.push({
                            id: "",
                            value: "",
                            valueDetail: presentNode.service,
                            count: index + 1,
                            refNodeId: presentNode.id,
                            nodeName: presentNode?.name,
                        });
                    }
                }
                setCountToNameMap((prev) => ({ ...prev, [index + 1]: workflowNodesMap[presentNode.id]?.node?.name || '' }));
            });
            setResponseConfig([...responseConfigArray]);
        }
    }, [nodeResponseConfigs, nodeIdsAbove]);

    const addNewCondition = () => {
        const newCondition: NodeExecutionCondition = {
            id: ``,
            created_time: new Date().toISOString(),
            updated_time: new Date().toISOString(),
            workflow: workflowNode.node.workflow!,
            user_id: workflowNode.node.user_id!,
            node_connection: workflowNode.nodeConnection!,
            node_data_key: "",
            condition: 0, // Default to "Equal"
            condition_check_data: "",
            condition_check_key: "",
            dynamic_condition: false
        };

        setLocalConditions([...localConditionsRef.current, newCondition]);
    };

    const removeCondition = (index: number) => {
        const _conditions = [...localConditionsRef.current];
        _conditions[index].id && setExecutionConditionsToDelete(prev => ([...prev, _conditions[index].id]));
        _conditions.splice(index, 1);
        setLocalConditions(_conditions);
    }

    const [filterText, setFilterText] = useState(""); // New state for filter input
    const [showSearchField, setShowSearchField] = useState(false);

    const shouldRenderFilter = showSearchField;
    const shouldRenderConnectAccount = actionMetadata.should_render_connection_step;
    const isTriggerEnabled = !!chosenAction && (!shouldRenderConnectAccount || isIntegrationPresent);

    const isTestRunInstancePresent = !!testRunInstance?.nodeTestRunInstance?.id || (isTriggerNode && !!testRunInputs?.length);
    const showTriggerSetupSuccess = (
        isSomeTriggerAdded ||
            Object.keys(nodeRunConfig).length == 0 ||
            workflowType == "filter" && Array.isArray(executionConditionsForNode) && executionConditionsForNode?.length > 0
    );

    // Modify the existing states
    const [oneShotPopperAnchorEl, setOneShotPopperAnchorEl] = useState<HTMLElement | null>(null);
    const oneShotPopperOpen = Boolean(oneShotPopperAnchorEl);

    // Add a method to toggle the popper
    const toggleOneShotSamplesPopper = (event?: React.MouseEvent<HTMLElement>) => {
        if (oneShotPopperOpen) {
            // If already open, close it
            setOneShotPopperAnchorEl(null);
        } else {
            // If closed, open it with the current target
            setOneShotPopperAnchorEl(event?.currentTarget || null);
        }
    };

    return (
        <div
            id={workflowNode.node?.id || workflowBoxIndex.toString()}
        >
            <div className={clsx("bg-white rounded-lg border border-solid border-borderGrey shadow-2xl hover:shadow-2xl transition-shadow duration-200", additionalClasses)}>
                <WorkflowBoxHeader
                    workflowType={workflowType}
                    workflowBoxIndex={workflowBoxIndex}
                    workflowTitle={workflowTitle}
                    chosenAction={chosenAction}
                    nodeConfigs={nodeConfigs}
                    nodeId={workflowNode.node?.id}
                    integrations={integrations}
                    onHeaderClick={() => setOpenDetails(!openDetails)}
                    workflowNode={workflowNode}
                    deleteNodeById={deleteNodeById}
                    shouldDeleteNode={shouldDeleteNode}
                    localConditions={localConditions}
                    refreshWorkflowNodes={refreshWorkflowNodes}
                    getHasChildren={getHasChildren}
                    hideMenu={true}
                />

                {
                    openDetails || keepAlwaysOpen ?
                        <>
                            <hr className="border-lightGrey" />
                            <div className="px-3">
                                <WorkflowAccordion
                                    title="Choose event"
                                    showNumber={chosenAction ? "" : "1"}
                                    showTick={!!chosenAction}
                                    children={
                                        <SelectAction
                                            chosenAction={chosenAction}
                                            actionsForService={actionsForService}
                                            workflowType={workflowType}
                                            nodeId={nodeId}
                                            getNodeConfigsForNodeFromRef={getNodeConfigsForNodeFromRef}
                                            onSave={(action) => onSave(action, () => {}, true)}
                                            nodeConfig={nodeRunConfig}
                                            isTriggerNode={isTriggerNode}
                                        />
                                    }
                                    setExpandedSection={setExpandedSection}
                                    expanded={expandedSection === "Choose event"}
                                />
                                {shouldRenderConnectAccount && (
                                    <TokenSelection
                                        nodeConfigs={nodeConfigs}
                                        nodeId={nodeId}
                                        expandedSection={expandedSection}
                                        chosenAction={chosenAction}
                                        currentToken={currentToken}
                                        tokenOptions={tokenOptions}
                                        setExpandedSection={setExpandedSection}
                                        handleTokenChange={handleTokenChange}
                                        workflowType={workflowType}
                                        customFetch={customFetch}
                                        workflowId={workflowId}
                                        workflowNode={workflowNode}
                                    />
                                )}
                                <WorkflowAccordion
                                    title="Set up trigger"
                                    showNumber={showTriggerSetupSuccess ? "" : shouldRenderConnectAccount ? "3" : "2"}
                                    showTick={showTriggerSetupSuccess}
                                    isDisabled={!isTriggerEnabled}
                                    setExpandedSection={chosenAction ? setExpandedSection : () => {}}
                                    expanded={expandedSection === "Set up trigger"}
                                    setShowSearchField={setShowSearchField}
                                    showSearchField={showSearchField}
                                    showSearchIcon={true}
                                    children={
                                        <>
                                            {shouldRenderFilter && (
                                                <div className="mb-4">
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            placeholder="Search fields"
                                                            value={filterText}
                                                            onChange={(e) => setFilterText(e.target.value)}
                                                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-300"
                                                        />
                                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                                            <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
                                                                <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                                                            </svg>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            {workflowType === 'webhook' ? (
                                                <div className="p-6 bg-[#F8F7FF] rounded-lg m-6">
                                                    <h3 className="text-xl font-semibold mb-2">Your webhook URL</h3>
                                                    <p className="text-gray-600 mb-4">You'll need to configure your application with this webhook URL</p>
                                                    <div className="flex items-center gap-2 p-4 bg-white rounded border border-gray-200">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={`${backendUrl}/services/webhook/${workflowNode.node?.id || ''}`}
                                                            className="flex-1 bg-transparent outline-none"
                                                        />
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(`${backendUrl}/services/webhook/${workflowNode.node?.id || ''}`)}
                                                            className="text-[#6366F1] font-medium hover:text-[#4F46E5]"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <p className="text-gray-600 mt-4">
                                                        We've generated a custom webhook URL for you to send requests to.
                                                        <br />
                                                        You would need to add a auth header, found in /apikeys
                                                    </p>
                                                </div>
                                            ) : workflowType === 'sesinbound' ? (
                                                <div className="p-6 bg-[#F8F7FF] rounded-lg m-6">
                                                    <h3 className="text-xl font-semibold mb-2">Your Email Address</h3>
                                                    <p className="text-gray-600 mb-4">Send emails to this address to trigger this node</p>
                                                    <div className="flex items-center gap-2 p-4 bg-white rounded border border-gray-200">
                                                        <input
                                                            type="text"
                                                            readOnly
                                                            value={`${workflowNode.node?.id || 'node-id'}@turinginbound.nanonets.com`}
                                                            className="flex-1 bg-transparent outline-none"
                                                        />
                                                        <button
                                                            onClick={() => navigator.clipboard.writeText(`${workflowNode.node?.id || 'node-id'}@turinginbound.nanonets.com`)}
                                                            className="text-[#6366F1] font-medium hover:text-[#4F46E5]"
                                                        >
                                                            Copy
                                                        </button>
                                                    </div>
                                                    <p className="text-gray-600 mt-4">
                                                        Emails sent to this address will automatically trigger this node.
                                                        <br />
                                                        The email content (subject, body, attachments) will be available in your workflow.
                                                    </p>
                                                </div>
                                            ) : workflowType === 'code' ? (
                                                <TriggerSetupCodeNode
                                                    nodeRunConfig={nodeRunConfig}
                                                    setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                    nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                    responseConfig={responseConfig}
                                                    chosenAction={chosenAction}
                                                    saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                    openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                    localConditions={localConditions}
                                                    removeCondition={removeCondition}
                                                    setLocalConditions={setLocalConditions}
                                                    isAnyStepIncomplete={isAnyStepIncomplete}
                                                    setConditionsOpenDetails={setConditionsOpenDetails}
                                                    addNewCondition={addNewCondition}
                                                    onSave={onSave}
                                                    workflowNode={workflowNode}
                                                    resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                    inputRows={inputRows}
                                                    setInputRows={setInputRows}
                                                    filterText={filterText}
                                                    nodeId={nodeId}
                                                    countToNameMap={countToNameMap}
                                                />
                                            ) : (workflowType === 'review' && chosenAction === 'WaitForConfirmation') ? (
                                                <>
                                                    <div className="p-6 bg-[#F8F7FF] rounded-lg m-6">
                                                        <h3 className="text-xl font-semibold mb-2">Your webhook URL</h3>
                                                        <p className="text-gray-600 mb-4">You'll need to configure your application with this webhook URL</p>
                                                        <div className="flex items-center gap-2 p-4 bg-white rounded border border-gray-200">
                                                            <input
                                                                type="text"
                                                                readOnly
                                                                value={`${backendUrl}/v1/services/review/confirm/:node_trigger_id`}
                                                                className="flex-1 bg-transparent outline-none"
                                                            />
                                                            <button
                                                                onClick={() => navigator.clipboard.writeText(`${backendUrl}/v1/services/review/confirm/:node_trigger_id`)}
                                                                className="text-[#6366F1] font-medium hover:text-[#4F46E5]"
                                                            >
                                                                Copy
                                                            </button>
                                                        </div>
                                                        <p className="text-gray-600 mt-4">
                                                            We've generated a custom webhook URL for you to send requests to.
                                                            <br />
                                                            You would need to add a auth header, found in /apikeys
                                                        </p>
                                                    </div>
                                                    <TriggerSetupWaitForConfirmation
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                        inputRows={inputRows}
                                                        setInputRows={setInputRows}
                                                        filterText={filterText}
                                                        nodeId={nodeId}
                                                        countToNameMap={countToNameMap}
                                                    />
                                                </>
                                            ) : (workflowType === 'nanonets' && chosenAction === 'ExtractDataFromFiles') ? (
                                                <TriggerSetupNanonetsExtractDataFromFiles
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                        inputRows={inputRows}
                                                        setInputRows={setInputRows}
                                                        filterText={filterText}
                                                        nodeId={nodeId}
                                                        countToNameMap={countToNameMap}
                                                        isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                                                        // setOneShotPopperAnchorEl={setOneShotPopperAnchorEl}
                                                    />
                                            ) : (workflowType === 'nanonets' && chosenAction === 'Update') ? (
                                                <TriggerSetupNanonetsUpdate
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                        inputRows={inputRows}
                                                        setInputRows={setInputRows}
                                                        filterText={filterText}
                                                        nodeId={nodeId}
                                                        countToNameMap={countToNameMap}
                                                        isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                                                    />
                                            ) : (workflowType === 'sharepoint' && chosenAction === 'UpdateMetadata') ? (
                                                <TriggerSetupSharepointUpdateMetadata
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                        inputRows={inputRows}
                                                        setInputRows={setInputRows}
                                                        filterText={filterText}
                                                        nodeId={nodeId}
                                                        countToNameMap={countToNameMap}
                                                        isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                                                    />
                                            ) : (workflowType === 'salesforce' && chosenAction === 'CreateCase') ? (
                                                <TriggerSetupSalesforceCreateCase
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        resetNodeRunConfigInRef={resetNodeRunConfigInRef}
                                                        inputRows={inputRows}
                                                        setInputRows={setInputRows}
                                                        filterText={filterText}
                                                        nodeId={nodeId}
                                                        countToNameMap={countToNameMap}
                                                        isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                                                    />
                                            ) : (
                                                <>
                                                    <TriggerSetup
                                                        nodeRunConfig={nodeRunConfig}
                                                        setNodeRunConfigAndRefNodeId={setNodeRunConfigAndRefNodeId}
                                                        nodeConfig={nodeConfigs?.[workflowNode.node?.id as string]}
                                                        responseConfig={responseConfig}
                                                        chosenAction={chosenAction}
                                                        saveConfigField={(key) => saveConfigField(workflowNode?.node?.id || '', key, refreshSavedRunConfig, setIsFetchingSavedNodeConfig)}
                                                        openConditionsDetails={openConditionsDetails || !!(Array.isArray(localConditions) && localConditions.length)}
                                                        localConditions={localConditions}
                                                        removeCondition={removeCondition}
                                                        setLocalConditions={setLocalConditions}
                                                        isAnyStepIncomplete={isAnyStepIncomplete}
                                                        setConditionsOpenDetails={setConditionsOpenDetails}
                                                        addNewCondition={addNewCondition}
                                                        onSave={onSave}
                                                        workflowNode={workflowNode}
                                                        filterText={filterText}
                                                        countToNameMap={countToNameMap}
                                                        isFetchingSavedNodeConfig={isFetchingSavedNodeConfig}
                                                        setOneShotPopperAnchorEl={toggleOneShotSamplesPopper}
                                                        action={chosenAction}
                                                        service={workflowType}
                                                    />
                                                </>
                                            )}
                                        </>
                                    }
                                />
                                <WorkflowAccordion
                                    title="Test this step"
                                    showNumber={isTestRunInstancePresent ? "" : shouldRenderConnectAccount ? "4" : "3"}
                                    isDisabled={isAnyStepIncomplete}
                                    setExpandedSection={!isAnyStepIncomplete ? setExpandedSection : () => {}}
                                    expanded={expandedSection === "Test this step"}
                                    showTick={isTestRunInstancePresent}
                                    children={
                                        <div className="p-4 flex flex-col gap-3 items-center border border-solid border-lightGrey rounded shadow-sm">
                                            <RunTest
                                                showSelectRunId={isTriggerNode}
                                                node={workflowNode.node}
                                                isClickDisabled={isAnyStepIncomplete}
                                                updateNodeConfigsFromRunInstances={updateNodeConfigsFromRunInstances}
                                                testRunInputs={testRunInputs}
                                                selectedRunInstanceId={selectedRunInstanceId}
                                                setSelectedRunInstanceId={setSelectedRunInstanceId}
                                                refetchTestRunInputs={refetchTestRunInputs}
                                                refetchTestRunInstance={refetchTestRunInstance}
                                                workflowType={workflowType}
                                                hasTestData={actionMetadata.has_test_data}
                                                testRunInstance={testRunInstance?.nodeTestRunInstance}
                                                testRunInstances={testRunInstances}
                                                getNodeConfigsForNodeFromRef={getNodeConfigsForNodeFromRef}
                                            />
                                        </div>
                                    }
                                />
                            </div>
                            {/* <div className="p-6 flex items-center gap-2">
                                <Help />
                                <FormHelperText className="flex-1">Troubleshoot</FormHelperText>
                                <Button variant="contained" className={clsx("!bg-nanonetsBlue !normal-case !text-white", { "opacity-60": isAnyStepIncomplete })}
                                    disabled={isAnyStepIncomplete}
                                    size="small"
                                    onClick={() => {
                                        onSave();
                                    }}
                                >
                                    {isAnyStepIncomplete ? "Finish setup to save." : "Save Trigger"}
                                </Button>
                            </div> */}
                        </>
                    : null
                }
            </div>
            <OneShotSamplesPopper
                open={oneShotPopperOpen}
                anchorEl={oneShotPopperAnchorEl}
                onClose={() => setOneShotPopperAnchorEl(null)}
                nodeId={nodeId}
                workflowId={workflowId}
            />
        </div>
    );
};

export default WorkflowBox;
